import DataExplorationIcon from '@mui/icons-material/DataExploration';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import AssetHistoryContent from '~/components/cars/AssetHistoryContent';
import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import { sumCounts, tabLabelWithCount, tabLabelWithIcon } from '~/components/common/TabCount';
import { TabPanel, useTabs } from '~/components/common/tabs';
import { TabContext, TabStrip } from '~/components/common/tabs/useTabs';
import { ElevatorComplianceTab } from '~/components/compliance/detailPage/ElevatorComplianceTab';
import { ElevatorContractsTab } from '~/components/compliance/detailPage/ElevatorContractsTab';
import { ElevatorMaintenanceTab } from '~/components/compliance/detailPage/ElevatorMaintenanceTab';
import { useCarRelatedCounts } from '~/components/compliance/detailPage/useCarRelatedCounts';
import { selectedTabSearchParam } from '~/components/global/constants';
import { ElevatorInvoicesTable } from '~/components/invoices/detailPage/related/ElevatorInvoicesTable';
import { ElevatorTicketsTable } from '~/components/invoices/detailPage/related/ElevatorTicketsTable';
import { CarKnowledgeTab } from '~/components/knowledge/KnowledgeTimeline';
import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { ServiceHistoryTab } from '~/components/serviceHistory/ServiceHistoryTab';
import { serviceHistoryTabLabel } from '~/components/serviceHistory/ServiceHistoryTabLabel';
import { ANY_TIME, type CreatedFilter } from '~/components/tasks/CreatedDateFilter';
import { createdInFilter } from '~/components/tasks/taskFilters';
import { CarTasksTab } from '~/components/tasks/TasksTab';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import { NeedsAttentionPanel } from '~/components/workQueue/NeedsAttentionPanel';
import { useNeedsAttentionItems } from '~/components/workQueue/useNeedsAttentionItems';
import { useCarKnowledge } from '~/data/hooks/useKnowledge';
import { useTasks } from '~/data/hooks/useTasks';
import type { API_Compliance } from '~/data/schemas/compliance';
import { InvoiceKind } from '~/data/schemas/invoice';
import { pageBandColor, SURFACE_RADIUS } from '~/theme';

enum Tab {
  NeedsAttention = 'Needs Attention',
  Knowledge = 'Knowledge',
  Tasks = 'Tasks',
  History = 'Asset History',
  Compliance = 'Compliance',
  // The tab strip labels this "Service History" since the callbacks sub-tab joined it.
  // The value is what the `tab` search param carries, so it stays as it is rather than
  // breaking links already pointing here.
  VisitsCompletion = 'Maintenance Completion',
  Maintenance = 'Service & Performance',
  Tickets = 'Tickets',
  Invoices = 'Invoices',
  Contracts = 'Contracts',
}

/**
 * Needs Attention first: the car's slice of the work queue is what there is to do here.
 * Asset History next, as the timeline that answers "what happened to this unit". Then
 * compliance and what has been raised against the car, billed to it and agreed over it,
 * each with its count.
 */
const allTabLabels = [
  Tab.NeedsAttention,
  Tab.Knowledge,
  Tab.Tasks,
  Tab.History,
  Tab.Compliance,
  Tab.VisitsCompletion,
  Tab.Maintenance,
  Tab.Tickets,
  Tab.Invoices,
  Tab.Contracts,
];

/**
 * Which tab a deep link asks for. The drawer took `carPanelTab=history` from the
 * property grid's name cell; the page takes the `tab` param every other detail surface
 * uses, and the redirects into this page translate the old key.
 */
export const ELEVATOR_HISTORY_TAB = Tab.History;

const isKnownTab = (tabLabels: Tab[], tab: string | null): tab is Tab =>
  tabLabels.includes(tab as Tab);

export function ElevatorTabContainer({
  compliance,
  onCertificateAdded,
}: {
  compliance: API_Compliance;
  onCertificateAdded: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromSearchParams = searchParams.get(selectedTabSearchParam);
  const knowledgeEnabled = useKnowledgeEnabled();
  const tasksEnabled = useTasksEnabled();
  const tabLabels = useMemo(
    () =>
      allTabLabels.filter(
        (tab) => (knowledgeEnabled || tab !== Tab.Knowledge) && (tasksEnabled || tab !== Tab.Tasks),
      ),
    [knowledgeEnabled, tasksEnabled],
  );
  // Both tables are keyed on a set of cars; this page's set is the one car.
  const carIds = useMemo(() => [compliance.carId], [compliance.carId]);

  const onTabChange = useCallback(
    (tabName: string) => {
      setSearchParams(
        (prev) => {
          if (tabName !== prev.get(selectedTabSearchParam)) {
            prev.set(selectedTabSearchParam, tabName);
          }
          return prev;
        },
        // Switching tabs is not a place to come back to — Back should leave the car, not walk
        // the tabs that were opened on it.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { context, labelToTabId, selectTab } = useTabs(
    tabLabels,
    isKnownTab(tabLabels, tabFromSearchParams) ? tabFromSearchParams : Tab.NeedsAttention,
    { onTabChange },
  );

  // React Router keeps the route mounted when only `:carId` changes, so reset to the
  // first tab when a different car opens. A tab named in the URL is the reader asking
  // for one, and still wins.
  const openedCar = useRef(compliance.carId);
  useEffect(() => {
    const isNewCar = compliance.carId !== openedCar.current;
    openedCar.current = compliance.carId;

    if (isKnownTab(tabLabels, tabFromSearchParams)) {
      selectTab(tabFromSearchParams);
    } else if (isNewCar) {
      selectTab(Tab.NeedsAttention);
    }
  }, [compliance.carId, tabFromSearchParams, selectTab, tabLabels]);

  const counts = useCarRelatedCounts({
    carId: compliance.carId,
    propertyId: compliance.propertyId,
  });
  const needsAttention = useNeedsAttentionItems(compliance.propertyId, compliance.carId);
  const knowledge = useCarKnowledge(compliance.carId);
  const currentKnowledgeCount = knowledge.entries.filter((e) => !e.supersededById).length;
  const tasks = useTasks({ carId: compliance.carId });
  const [taskCreated, setTaskCreated] = useState<CreatedFilter>(ANY_TIME);
  const taskCount = tasks.tasks.filter((task) => createdInFilter(task, taskCreated)).length;

  const overrideLabels = useMemo(
    () => ({
      [Tab.NeedsAttention]: tabLabelWithCount('Needs Attention', needsAttention.count),
      [Tab.Knowledge]: tabLabelWithCount(
        'Knowledge',
        knowledge.isLoading || knowledge.error ? undefined : currentKnowledgeCount,
      ),
      [Tab.Tasks]: tabLabelWithCount(
        'Custom Tasks',
        tasks.isLoading || tasks.error ? undefined : taskCount,
      ),
      // Open violations, not every violation the car has ever had — the resolved ones are
      // counted on their own subtab inside.
      [Tab.Compliance]: tabLabelWithCount('Compliance', compliance.openViolations),
      [Tab.VisitsCompletion]: serviceHistoryTabLabel(),
      [Tab.Maintenance]: tabLabelWithIcon(
        Tab.Maintenance,
        <DataExplorationIcon sx={{ fontSize: 20 }} />,
      ),
      [Tab.Tickets]: tabLabelWithCount('Tickets', counts.tickets),
      // The enum value stays 'Invoices' because it is the `tab` search param older links
      // carry. Undefined until both subtabs are known, so it never counts up in steps.
      [Tab.Invoices]: tabLabelWithCount(
        'Spendings',
        sumCounts(counts.invoices, counts.proposals, counts.credits),
      ),
      [Tab.Contracts]: tabLabelWithCount('Contracts', counts.contracts),
      // Every event on the car, whatever the filter inside the tab is set to.
      [Tab.History]: tabLabelWithCount('Asset History', counts.history),
    }),
    [
      needsAttention.count,
      knowledge.isLoading,
      knowledge.error,
      currentKnowledgeCount,
      tasks.isLoading,
      tasks.error,
      taskCount,
      compliance.openViolations,
      counts.tickets,
      counts.invoices,
      counts.proposals,
      counts.credits,
      counts.contracts,
      counts.history,
    ],
  );

  return (
    // `minWidth: 0` here and on the panel row: a flex item defaults to `min-width: auto`,
    // so content wider than the window stretches every ancestor instead of scrolling
    // inside its own viewport.
    <Stack flex={1} minWidth={0}>
      <TabContext.Provider value={context}>
        <TabStrip
          overrideLabels={overrideLabels}
          styles={{
            tabs: {
              backgroundColor: pageBandColor,
              px: { xs: 2, md: 3 },
              borderBottomLeftRadius: SURFACE_RADIUS,
              borderBottomRightRadius: SURFACE_RADIUS,
            },
          }}
        />
        {/* Must be a column flex container: a row one with overflow drops the cross-axis
          (bottom) padding of its scrolled content, clipping each tab's own pb.

          No `overflow: auto`: the page itself scrolls, up to the point where the strip
          above pins, and a scroll box here would take that scrolling off the page. */}
        <Box flex={1} minWidth={0} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TabPanel tabId={labelToTabId[Tab.NeedsAttention]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <NeedsAttentionPanel
                items={needsAttention.items}
                isLoading={needsAttention.isLoading}
                isValidating={needsAttention.isValidating}
                snooze={needsAttention.snooze}
                refresh={needsAttention.refresh}
                emptyMessage="Nothing on this unit needs attention right now."
              />
            </Box>
          </TabPanel>
          {knowledgeEnabled ? (
            <TabPanel tabId={labelToTabId[Tab.Knowledge]}>
              <Box sx={{ p: 3, width: '100%' }}>
                <CarKnowledgeTab carId={compliance.carId} propertyId={compliance.propertyId} />
              </Box>
            </TabPanel>
          ) : null}
          {tasksEnabled ? (
            <TabPanel tabId={labelToTabId[Tab.Tasks]}>
              <Box sx={{ p: 3, width: '100%' }}>
                <CarTasksTab
                  carId={compliance.carId}
                  propertyId={compliance.propertyId}
                  propertyName={compliance.property ?? ''}
                  portfolioName={compliance.portfolio}
                  created={taskCreated}
                  onCreatedChange={setTaskCreated}
                />
              </Box>
            </TabPanel>
          ) : null}
          <TabPanel tabId={labelToTabId[Tab.Compliance]}>
            <ElevatorComplianceTab
              compliance={compliance}
              onCertificateAdded={onCertificateAdded}
            />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.VisitsCompletion]}>
            <ServiceHistoryTab
              scope={{ car: compliance.carId }}
              visitsEmptyMessage="This elevator has no maintenance periods in this window."
              callbacksEmptyMessage="This elevator is not covered for the selected period."
            />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Maintenance]}>
            <ElevatorMaintenanceTab compliance={compliance} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Tickets]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <ElevatorTicketsTable
                previewList="elevator_tickets"
                carIds={carIds}
                emptyMessage="This unit has no ticket history."
              />
            </Box>
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Invoices]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <SectionTabs
                ariaLabel="Invoice, proposal and credit views"
                tabs={[
                  {
                    value: 'invoices',
                    label: withCount('Invoices', counts.invoices),
                    render: () => (
                      <ElevatorInvoicesTable
                        carIds={carIds}
                        kind={InvoiceKind.Invoice}
                        emptyMessage="Nothing has been billed to this unit."
                      />
                    ),
                  },
                  {
                    value: 'proposals',
                    label: withCount('Proposals', counts.proposals),
                    render: () => (
                      <ElevatorInvoicesTable
                        carIds={carIds}
                        kind={InvoiceKind.Proposal}
                        emptyMessage="Nothing has been proposed for this unit."
                      />
                    ),
                  },
                  {
                    value: 'credits',
                    label: withCount('Credits', counts.credits),
                    render: () => (
                      <ElevatorInvoicesTable
                        carIds={carIds}
                        kind={InvoiceKind.Credit}
                        emptyMessage="No credits apply to invoices for this unit."
                      />
                    ),
                  },
                ]}
              />
            </Box>
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.Contracts]}>
            <ElevatorContractsTab compliance={compliance} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[Tab.History]}>
            <AssetHistoryContent carId={compliance.carId} />
          </TabPanel>
        </Box>
      </TabContext.Provider>
    </Stack>
  );
}
